import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, catchError, finalize, forkJoin, of, timeout } from 'rxjs';
import { MaintenancePart, MaintenanceSchedulingService } from './maintenance-scheduling.service';

interface CalendarCell {
  dayNumber: number;
  isoDate: string;
  inCurrentMonth: boolean;
  isSelected: boolean;
}

@Component({
  selector: 'app-schedule-maintenance',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './schedule-maintenance.view.html',
  styleUrls: ['./schedule-maintenance.view.css']
})
export class ScheduleMaintenanceComponent implements OnInit, OnDestroy {
  private readonly loadTimeoutMs = 12000;
  readonly weekdayLabels = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
  readonly monthOptions = [
    { value: 0, label: 'Jan' },
    { value: 1, label: 'Feb' },
    { value: 2, label: 'Mar' },
    { value: 3, label: 'Apr' },
    { value: 4, label: 'May' },
    { value: 5, label: 'Jun' },
    { value: 6, label: 'Jul' },
    { value: 7, label: 'Aug' },
    { value: 8, label: 'Sep' },
    { value: 9, label: 'Oct' },
    { value: 10, label: 'Nov' },
    { value: 11, label: 'Dec' }
  ];
  ownerId = '';
  vehicleId: number | undefined;
  selectedPartName = '';
  selectedPart: MaintenancePart | null = null;
  selectedMechanic = '';

  scheduledDate = this.todayDate();
  fromHour = '08';
  fromMinute = '00';
  fromPeriod = 'AM';
  toHour = '10';
  toMinute = '00';
  toPeriod = 'AM';
  calendarMonth = new Date().getMonth();
  calendarYear = new Date().getFullYear();
  calendarCells: CalendarCell[] = [];

  errorMessage = '';
  successMessage = '';
  loadingPart = false;
  submitting = false;
  private routeSub: Subscription | null = null;
  private loadSub: Subscription | null = null;
  private scheduleSub: Subscription | null = null;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly schedulingService: MaintenanceSchedulingService
  ) {}

  ngOnInit(): void {
    this.routeSub = this.route.queryParamMap.subscribe((params) => {
      this.ownerId = (params.get('ownerId') ?? '').trim();
      const rawVehicleId = params.get('vehicleId');
      const parsedVehicleId = rawVehicleId ? Number.parseInt(rawVehicleId, 10) : Number.NaN;
      this.vehicleId = Number.isFinite(parsedVehicleId) ? parsedVehicleId : undefined;

      this.selectedPartName = (params.get('part') ?? 'Engine Oil').trim() || 'Engine Oil';
      const selectedMechanicFromRoute = (params.get('mechanic') ?? '').trim();
      // Keep calendar state in sync whenever routing swaps part/mechanic context.
      this.syncCalendarFromScheduledDate();
      this.loadSelectedPart(selectedMechanicFromRoute);
    });
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
    this.loadSub?.unsubscribe();
    this.scheduleSub?.unsubscribe();
  }

  get fromTimeLabel(): string {
    return `${this.fromHour}:${this.fromMinute} ${this.fromPeriod}`;
  }

  get toTimeLabel(): string {
    return `${this.toHour}:${this.toMinute} ${this.toPeriod}`;
  }

  get selectedMechanicLabel(): string {
    return this.selectedMechanic || 'Choose mechanic';
  }

  get calendarYearOptions(): number[] {
    const currentYear = new Date().getFullYear();
    const yearSet = new Set<number>();
    for (let year = currentYear - 2; year <= currentYear + 6; year++) {
      yearSet.add(year);
    }
    yearSet.add(this.calendarYear);

    return Array.from(yearSet).sort((a, b) => a - b);
  }

  goBack(): void {
    this.router.navigate(['/maintenance-parts'], { queryParams: this.sharedQueryParams() });
  }

  partIcon(partName: string): string {
    const normalizedPartName = partName.trim().toLowerCase();
    if (normalizedPartName.includes('spark')) {
      return 'assets/dashboard/iconSpark.svg';
    }

    if (normalizedPartName.includes('brake')) {
      return 'assets/dashboard/iconBrake.svg';
    }

    if (normalizedPartName.includes('cabin')) {
      return 'assets/dashboard/iconCabinFilter.svg';
    }

    if (normalizedPartName.includes('engine air')) {
      return 'assets/dashboard/iconEngineFilter.svg';
    }

    if (normalizedPartName.includes('fuel')) {
      return 'assets/dashboard/iconFuel.svg';
    }

    if (normalizedPartName.includes('oil')) {
      return 'assets/dashboard/iconOil.svg';
    }

    return 'assets/dashboard/iconCabinFilter.svg';
  }

  onPrevMonth(): void {
    if (this.calendarMonth === 0) {
      this.calendarMonth = 11;
      this.calendarYear -= 1;
    } else {
      this.calendarMonth -= 1;
    }

    this.rebuildCalendarCells();
  }

  onNextMonth(): void {
    if (this.calendarMonth === 11) {
      this.calendarMonth = 0;
      this.calendarYear += 1;
    } else {
      this.calendarMonth += 1;
    }

    this.rebuildCalendarCells();
  }

  onCalendarMonthChange(nextMonth: number): void {
    this.calendarMonth = Number(nextMonth);
    this.rebuildCalendarCells();
  }

  onCalendarYearChange(nextYear: number): void {
    this.calendarYear = Number(nextYear);
    this.rebuildCalendarCells();
  }

  onSelectCalendarDay(cell: CalendarCell): void {
    if (!cell.inCurrentMonth) {
      return;
    }

    this.scheduledDate = cell.isoDate;
    this.rebuildCalendarCells();
  }

  openMechanics(): void {
    this.router.navigate(['/select-mechanic'], {
      queryParams: {
        part: this.selectedPartName,
        mechanic: this.selectedMechanic,
        ...this.sharedQueryParams()
      }
    });
  }

  schedule(): void {
    this.errorMessage = '';
    this.successMessage = '';

    if (!this.selectedPart) {
      this.errorMessage = 'Select a valid maintenance part.';
      return;
    }

    if (!this.selectedMechanic.trim()) {
      this.errorMessage = 'Select a mechanic before scheduling.';
      return;
    }

    if (!this.ownerId.trim()) {
      this.errorMessage = 'Owner account is required to schedule maintenance.';
      return;
    }

    this.submitting = true;
    this.scheduleSub?.unsubscribe();
    this.scheduleSub = this.schedulingService
      .scheduleMaintenance(
        this.ownerId,
        this.vehicleId,
        this.selectedPart.name,
        this.selectedMechanic,
        this.scheduledDate,
        this.fromTimeLabel,
        this.toTimeLabel
      )
      .subscribe({
        next: () => {
          this.successMessage = 'Reservation submitted for mechanic approval.';
          this.submitting = false;
          this.router.navigate(['/maintenance-parts'], { queryParams: this.sharedQueryParams() });
        },
        error: () => {
          this.errorMessage = 'Unable to schedule maintenance.';
          this.submitting = false;
        }
      });
  }

  cancel(): void {
    this.router.navigate(['/maintenance-parts'], { queryParams: this.sharedQueryParams() });
  }

  private sharedQueryParams(): Record<string, string | number> {
    const queryParams: Record<string, string | number> = {};
    if (this.ownerId) {
      queryParams['ownerId'] = this.ownerId;
    }
    if (this.vehicleId !== undefined) {
      queryParams['vehicleId'] = this.vehicleId;
    }
    return queryParams;
  }

  private loadSelectedPart(routeMechanic: string): void {
    this.loadSub?.unsubscribe();
    const presetPart = this.schedulingService.getPresetPartDetail(this.selectedPartName);
    this.loadingPart = true;
    this.errorMessage = '';

    this.loadSub = forkJoin({
      part: this.schedulingService.getPart(this.selectedPartName, this.ownerId, this.vehicleId),
      mechanic: this.schedulingService.getScheduledMechanic(this.selectedPartName, this.ownerId, this.vehicleId)
    })
      .pipe(
        timeout(this.loadTimeoutMs),
        catchError(() => {
          const fallbackPart = this.selectedPart ?? presetPart;
          if (fallbackPart) {
            this.errorMessage = 'Live maintenance data is slow. Showing preset maintenance defaults.';
          } else {
            this.errorMessage = 'Unable to load maintenance details.';
          }

          return of({
            part: fallbackPart,
            mechanic: routeMechanic || this.selectedMechanic || this.resolveDefaultMechanic(fallbackPart)
          });
        }),
        finalize(() => {
          this.loadingPart = false;
        })
      )
      .subscribe({
        next: ({ part, mechanic }) => {
          const resolvedPart = part ?? presetPart;
          this.selectedPart = resolvedPart;
          this.selectedMechanic = routeMechanic || mechanic || this.resolveDefaultMechanic(resolvedPart);
          if (!resolvedPart) {
            this.errorMessage = 'Select a valid maintenance part.';
          }
        },
        error: () => {
          this.selectedPart = presetPart;
          this.selectedMechanic = routeMechanic || this.resolveDefaultMechanic(presetPart);
          this.errorMessage = presetPart
            ? 'Live maintenance data is slow. Showing preset maintenance defaults.'
            : 'Unable to load maintenance details.';
        }
      });
  }

  private todayDate(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private syncCalendarFromScheduledDate(): void {
    const parsedDate = this.parseIsoDate(this.scheduledDate) ?? new Date();
    this.calendarYear = parsedDate.getFullYear();
    this.calendarMonth = parsedDate.getMonth();
    this.rebuildCalendarCells();
  }

  private parseIsoDate(value: string): Date | null {
    const parsedDate = new Date(value);
    if (!Number.isFinite(parsedDate.getTime())) {
      return null;
    }

    return parsedDate;
  }

  private toIsoDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private rebuildCalendarCells(): void {
    // Render a fixed 6x7 month grid to keep layout stable across months.
    const firstDayOfMonth = new Date(this.calendarYear, this.calendarMonth, 1);
    const firstWeekday = firstDayOfMonth.getDay();
    const gridStartDate = new Date(this.calendarYear, this.calendarMonth, 1 - firstWeekday);
    const selectedDate = this.parseIsoDate(this.scheduledDate);

    this.calendarCells = Array.from({ length: 42 }, (_, offset) => {
      const cellDate = new Date(
        gridStartDate.getFullYear(),
        gridStartDate.getMonth(),
        gridStartDate.getDate() + offset
      );
      const isoDate = this.toIsoDate(cellDate);
      const inCurrentMonth = cellDate.getMonth() === this.calendarMonth;
      const isSelected =
        selectedDate !== null &&
        cellDate.getFullYear() === selectedDate.getFullYear() &&
        cellDate.getMonth() === selectedDate.getMonth() &&
        cellDate.getDate() === selectedDate.getDate();

      return {
        dayNumber: cellDate.getDate(),
        isoDate,
        inCurrentMonth,
        isSelected
      };
    });
  }

  private resolveDefaultMechanic(part: MaintenancePart | null): string {
    if (part) {
      const recentMechanic = part.history.find((entry) => entry.mechanic.trim().length > 0)?.mechanic;
      if (recentMechanic) {
        return recentMechanic;
      }
    }

    return this.schedulingService.getFallbackMechanicsSnapshot()[0]?.name ?? '';
  }
}
